import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import {useLocation, useNavigate} from 'react-router';
import {getUsersByIds} from '../../../firebase/firestore/users';
import {useSession} from '../../../firebase/UserProvider';
import PersonCell, {indexUsersById} from '../../collection/components/PersonCell';
import {statusColor, statusLabel} from '../../collection/collectionTaskPayload';
import useCollectionMaterialViewer from '../../collection/viewer/useCollectionMaterialViewer';
import openCollectionIir from '../../collection/viewer/openCollectionIir';
import CoverageBar from '../coverage/components/CoverageBar';
import EeiCollectionMaterialRow from './EeiCollectionMaterialRow';

/**
 * Compact list of collection tasks (with nested CMs and IIRs) for ICR/EEI pages.
 * On the EEI Collection tab, optional coverage props render a progress bar plus
 * Covered / Not covered lines (Create Collection Task sits on its own line below).
 */
export default function RequirementCollectionPanel({
    tasks = [],
    loading = false,
    error = null,
    coveragePct = null,
    coveredSummary = null,
    uncoveredSummary = null,
    gapText = null,
    canCreateTask = false,
    onCreateTask,
    /** When false, skip CT list / empty-state (Gap Collection = coverage only). */
    showTaskList = true,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {user} = useSession() || {};
    const {openCollectionMaterial} = useCollectionMaterialViewer();
    const [people, setPeople] = useState({});

    const assigneeIds = useMemo(
        () => [...new Set((tasks || []).map((t) => t.assigneeId).filter(Boolean))],
        [tasks],
    );

    useEffect(() => {
        if (!assigneeIds.length) {
            setPeople({});
            return undefined;
        }
        let cancelled = false;
        getUsersByIds(assigneeIds)
            .then((users) => {
                if (!cancelled) setPeople(indexUsersById(users || []));
            })
            .catch(() => {
                if (!cancelled) setPeople({});
            });
        return () => {
            cancelled = true;
        };
    }, [assigneeIds]);

    // Exact prior state so CT Back / C2 viewer close restore this page and tab.
    const returnTo = `${location.pathname}${location.search || ''}`;

    const goTask = (task) => {
        const key = task?.ctNumber || task?.id;
        if (!key) return;
        navigate(`/collection/tasks/${encodeURIComponent(key)}`, {state: {returnTo}});
    };

    const openMaterial = (item, kind) => {
        if (!item?.id) return;
        if (kind === 'iir') {
            openCollectionIir({
                iir: item,
                userId: user?.uid,
                returnTo,
                navigate,
                location,
            });
            return;
        }
        openCollectionMaterial({
            materialId: item.id,
            materialKind: kind,
            returnTo,
        });
    };

    if (loading) {
        return (
            <Stack alignItems="center" py={4} spacing={1}>
                <CircularProgress size={28}/>
                <Typography variant="body2" color="text.secondary">Loading collection work…</Typography>
            </Stack>
        );
    }

    if (error) {
        return (
            <Alert severity="error" variant="outlined">
                {error}
            </Alert>
        );
    }

    const list = Array.isArray(tasks) ? tasks : [];
    const covered = String(coveredSummary || '').trim();
    const uncovered = String(uncoveredSummary || gapText || '').trim();
    const hasCoveragePct = coveragePct != null && Number.isFinite(Number(coveragePct));
    const showCoverage = hasCoveragePct || Boolean(covered) || Boolean(uncovered);
    const showCreateOnUncovered = Boolean(uncovered && canCreateTask && onCreateTask);

    return (
        <Stack spacing={2}>
            {showCoverage && (
                <Stack spacing={1}>
                    {hasCoveragePct && (
                        <CoverageBar
                            value={Number(coveragePct)}
                            label="Data"
                            height={5}
                            showValue
                        />
                    )}
                    {(covered || uncovered) && (
                        <Stack spacing={0.5}>
                            {covered && (
                                <Typography variant="body2">
                                    <Box component="span" fontWeight={700}>Covered: </Box>
                                    {covered}
                                </Typography>
                            )}
                            {uncovered && (
                                <Typography variant="body2" color="text.secondary">
                                    <Box component="span" fontWeight={700} color="text.primary">Not covered: </Box>
                                    {uncovered}
                                </Typography>
                            )}
                            {showCreateOnUncovered && (
                                <Box>
                                    <Button size="small" variant="contained" onClick={onCreateTask}>
                                        Create Collection Task
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    )}
                </Stack>
            )}

            {/* Standalone Create CT when coverage UI is absent but the caller still opts in */}
            {!showCoverage && canCreateTask && onCreateTask && (
                <Box>
                    <Button size="small" variant="contained" onClick={onCreateTask}>
                        Create Collection Task
                    </Button>
                </Box>
            )}

            {showTaskList && list.length === 0 && (
                <Typography variant="body2" color="text.secondary" py={1}>
                    No collection tasks linked to this requirement
                </Typography>
            )}
            {showTaskList && list.length > 0 && (
                <Stack spacing={1.5}>
                    {list.map((task) => {
                        const materials = Array.isArray(task.materials) ? task.materials : [];
                        const iirs = Array.isArray(task.iirs) ? task.iirs : [];
                        return (
                            <Box
                                key={task.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.5,
                                }}
                            >
                                <Stack
                                    direction={{xs: 'column', sm: 'row'}}
                                    spacing={1}
                                    alignItems={{xs: 'flex-start', sm: 'center'}}
                                    justifyContent="space-between"
                                    sx={{cursor: 'pointer'}}
                                    onClick={() => goTask(task)}
                                >
                                    <Stack spacing={0.25} minWidth={0} flex={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography
                                                variant="subtitle2"
                                                fontFamily="Roboto Mono, monospace"
                                                fontWeight={700}
                                                color="primary"
                                            >
                                                {task.ctNumber || task.id}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={statusLabel(task.status) || task.status || '—'}
                                                color={statusColor(task.status)}
                                                sx={{height: 22}}
                                            />
                                        </Stack>
                                        <Typography variant="body2" noWrap title={task.title}>
                                            {task.title || 'Untitled task'}
                                        </Typography>
                                    </Stack>
                                    <Box onClick={(e) => e.stopPropagation()} sx={{flexShrink: 0}}>
                                        {task.assigneeId ? (
                                            <PersonCell
                                                userId={task.assigneeId}
                                                profile={people[task.assigneeId]}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">
                                                Unassigned
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>

                                {(materials.length > 0 || iirs.length > 0) && (
                                    <Stack spacing={0} mt={1.25} pl={{xs: 0.5, sm: 1.5}}>
                                        {materials.map((m) => (
                                            <EeiCollectionMaterialRow
                                                key={`cm-${m.id}`}
                                                item={{...m, materialKind: m.materialKind || 'cm'}}
                                                onOpen={(item) => openMaterial(item, 'cm')}
                                            />
                                        ))}
                                        {iirs.map((iir) => (
                                            <EeiCollectionMaterialRow
                                                key={`iir-${iir.id}`}
                                                item={{...iir, materialKind: 'iir'}}
                                                onOpen={(item) => openMaterial(item, 'iir')}
                                            />
                                        ))}
                                    </Stack>
                                )}

                                {materials.length === 0 && iirs.length === 0 && (
                                    <Typography variant="caption" color="text.disabled" sx={{mt: 1, display: 'block', pl: 0.5}}>
                                        No materials yet ·{' '}
                                        <Link
                                            component="button"
                                            variant="caption"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                goTask(task.id);
                                            }}
                                        >
                                            Open task
                                        </Link>
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {!showTaskList && !showCoverage && !canCreateTask && (
                <Typography variant="body2" color="text.secondary" py={1}>
                    No data coverage available for this requirement
                </Typography>
            )}
        </Stack>
    );
}
